import i18next from "i18next";

interface TranslatableField {
  [key: string]: string;
}

/**
 * Returns the translated value for a field based on the current language.
 * @param {TranslatableField | string} field The field to translate. It can be an object with language codes as keys or a simple string.
 * @param {string} [language] Optional language code to use. Defaults to the current i18next language.
 * @returns {string} The translated string or a fallback.
 */
export const getTranslatedData = (
  field: TranslatableField | string | undefined | null,
  language?: string,
): string => {
  if (!field) {
    return "";
  }

  // If the field is just a regular string, return it directly.
  if (typeof field === "string") {
    return field;
  }

  // Determine the language to use.
  const lang = language || i18next.language;

  // Try to get the translation for the specific language (e.g., "tr", "en").
  if (field[lang]) {
    return field[lang];
  }

  // Fallback for region-specific codes (e.g., "en-US" -> "en").
  const baseLang = lang.split("-")[0];
  if (field[baseLang]) {
    return field[baseLang];
  }

  // If no specific translation is found, fallback to the source language (tr),
  // then English, or the first available value in the object.
  return field.tr || field.en || Object.values(field)[0] || "";
};

export const setTranslatedData = (data: any, fieldName: string, lang: string, value: any): any => {
  const newData = { ...data };
  const currentField = newData[fieldName];

  if (typeof currentField === 'object' && currentField !== null && !Array.isArray(currentField)) {
    // Eğer alan zaten bir obje ise, ilgili dilin değerini güncelle
    newData[fieldName] = {
      ...currentField,
      [lang]: value,
    };
  } else {
    // Eğer alan bir string veya tanımsız ise, yeni bir obje oluştur
    const existingValue = currentField || ''; // Mevcut değeri koru
    newData[fieldName] = {
      tr: lang === 'tr' ? value : (typeof currentField === 'string' ? existingValue : ''),
      en: lang === 'en' ? value : (typeof currentField === 'string' ? existingValue : ''),
      ar: lang === 'ar' ? value : (typeof currentField === 'string' ? existingValue : ''),
      [lang]: value, // Seçili dili tekrar ayarla
    };
    // Mevcut string değeri, objenin diğer dillerine ata
    if(typeof currentField === 'string'){
      if(lang !== 'tr') newData[fieldName].tr = existingValue;
      if(lang !== 'en') newData[fieldName].en = existingValue;
      if(lang !== 'ar') newData[fieldName].ar = existingValue;
    }
  }

  return newData;
}; 